import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';
import useAlert from '../../hooks/utility/useAlert';
import { StoreInterface } from '../../store/types';
import CheckmarkTextInput from '../inputs/CheckmarkTextInput';
import { ButtonTypes, CouponDetailsInterface } from '../../services/exports/Interfaces';
import CompanyManager from '../../services/api/CompanyManager';
import { COUPON_TYPES, PROMO_CODE_TYPES } from '../../services/exports/Constants';
import MultiPromoCodeModal from '../../modals/MultiPromoCodeModal';
import useBasket from '../../hooks/basket/useBasket';
import MinimumValueErrorLabel from '../labels/MinimumValueErrorLabel';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useAuth from '../../hooks/global/useAuth';
import CashbackToggle from './CashbackToggle';
import { ReservedHttpResponseCodes } from '../../services/exports/Types';
import CustomButton from '../buttons/CustomButton';
import Highlighter from 'react-highlight-words';
import collect from 'collect.js';
import toast from 'react-hot-toast';

interface Props {
  showCashbackToggle?: boolean;
  className?: string;
}

const PromoCodeInput = forwardRef((props: Props, ref) => {
  useImperativeHandle(ref, () => ({ setPromoCode, removePromoCode }));

  const { t, i18n } = useTranslation(null, { keyPrefix: 'Components:Checkout:PromoCodeInput' });

  const { showCashbackToggle, className } = props;

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const alert = useAlert();
  const { formatCurrency, toggleLoginModal } = useAppMethods();
  const { isLoggedIn } = useAuth();
  const { subtotal } = useBasket();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [promoCodeInput, setPromoCodeInput] = useState<string>(cached_order?.coupon_details?.code ?? '');
  const [isApplied, setIsApplied] = useState<boolean>(!!cached_order?.coupon_details?.code);
  const [showMultiPromoCodeModal, setShowMultiPromoCodeModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const highlightedDiscountLabel = useMemo<string[]>(
    () =>
      collect()
        .when(cached_order?.coupon_type === COUPON_TYPES.CASHBACK, (collection) =>
          collection.push(t('discount_types.cashback')),
        )
        .when(cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE, (collection) =>
          collection.push(t('discount_types.promo_code')),
        )
        .toArray(),
    [cached_order?.coupon_type, i18n.language],
  );

  useEffect(() => {
    if (cached_order?.coupon_type !== COUPON_TYPES.PROMO_CODE && isApplied) {
      setPromoCodeInput('');
      setIsApplied(false);
    }
  }, [cached_order?.coupon_type, cached_order?.coupon_details?.code]);

  useEffect(() => {
    if (cached_order?.coupon_details?.code && profile?.id) {
      actualizePromoCode();
    }
  }, []);

  const actualizePromoCode = async () => {
    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(
      company.id,
      cached_order.coupon_details.code,
    );
    setIsLoading(false);

    if (!success) {
      removePromoCode();

      return;
    }

    const cachedComponent = cached_order.coupon_details.components.find((component) => !component.is_used);
    const actualComponent = response.data.data.components.find((component) => !component.is_used);

    if (cachedComponent.id !== actualComponent.id) {
      setPromoCode(response.data.data);
    }
  };

  const applyPromoCode = async (code?: string): Promise<any> => {
    if (!isLoggedIn) {
      return setShowLoginPrompt(true);
    }

    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(company.id, code ?? promoCodeInput);
    setIsLoading(false);

    if (!success) {
      applyPromoErrorHandler(response);

      return;
    }

    setPromoCode(response.data.data);
    toast.success(t('feedback.promo_code_added'));
  };

  const onLogin = () => {
    if (isLoggedIn) {
      return;
    }

    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  function applyPromoErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }

    removePromoCode();
  }

  const setPromoCode = (promoCode: CouponDetailsInterface) => {
    if (!isLoggedIn) {
      return setShowLoginPrompt(true);
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.PROMO_CODE,
        coupon_details: promoCode,
      }),
    );

    setPromoCodeInput(promoCode.code);
    setIsApplied(true);

    if (promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
      toggleMultiPromoCodeModal();
    }
  };

  const removePromoCode = (): void => {
    setPromoCodeInput('');

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: null,
        coupon_details: null,
      }),
    );

    setIsApplied(false);
  };

  const toggleMultiPromoCodeModal = () => {
    setShowMultiPromoCodeModal(!showMultiPromoCodeModal);
  };

  const renderMultiPromoCodeModal = useMemo(() => {
    return <MultiPromoCodeModal openModal={showMultiPromoCodeModal} toggleModal={toggleMultiPromoCodeModal} />;
  }, [showMultiPromoCodeModal]);

  const hasSubtotalExceededThreshold = (): boolean => {
    if (!cached_order?.coupon_details?.components) {
      return false;
    }

    const component = cached_order.coupon_details.components.find((component) => !component.is_used);

    return component && subtotal >= component.usage_threshold;
  };

  const renderMinimumValueError = useMemo(() => {
    if (
      !cached_order?.coupon_details ||
      hasSubtotalExceededThreshold() ||
      !cached_order?.coupon_details?.components ||
      cached_order?.coupon_details?.components?.length === 0
    ) {
      return null;
    }

    return (
      <MinimumValueErrorLabel
        description={t('feedback.usage_threshold')}
        value={formatCurrency(cached_order?.coupon_details?.components[0]?.usage_threshold)}
        className="mt-xs"
        type="Notice"
      />
    );
  }, [cached_order?.coupon_details, subtotal]);

  return (
    <div className={className}>
      <label htmlFor="promo-code" className="text-gray-700 text-sm">
        <Highlighter
          highlightTag="span"
          highlightClassName="font-medium underline text-black"
          autoEscape={true}
          searchWords={highlightedDiscountLabel}
          textToHighlight={t('input.label', {
            cashbackLabel: t('discount_types.cashback'),
            promoCodeLabel: t('discount_types.promo_code'),
          })}
        />
      </label>
      {showCashbackToggle && <CashbackToggle subtotal={subtotal} className="mt-sm" />}
      <div className="flex gap-xs mt-xs">
        <CheckmarkTextInput
          type="uppercase"
          placeholder={t('input.placeholder')}
          value={promoCodeInput}
          onChange={setPromoCodeInput}
          disabled={isApplied}
          id="promo-code"
          classes={{
            container: 'w-full',
            inputWrapper: 'lg:py-xs',
          }}
        />
        <CustomButton
          title={isApplied ? t('buttons.remove') : t('buttons.apply')}
          buttonType={isApplied ? ButtonTypes.danger : ButtonTypes.primary}
          onClick={isApplied ? removePromoCode : applyPromoCode}
          loading={isLoading}
          disabled={promoCodeInput?.length === 0}
          className="py-2xs rounded-lg !w-fit"
        />
      </div>
      {showLoginPrompt && !isLoggedIn && (
        <p className="mt-md text-xs font-medium text-danger">
          <Trans
            t={t}
            components={{
              highlight: <button className="underline" onClick={onLogin} />,
            }}
          >
            feedback.login
          </Trans>
        </p>
      )}
      {renderMinimumValueError}
      {renderMultiPromoCodeModal}
    </div>
  );
});

export default PromoCodeInput;
